// @ts-nocheck
/**
 * Host catalog of Overview recipes — slot ids, roles, defaults.
 * Shared by runtime, validator, and template editor.
 */

import type { LocalizedText } from '../types/metadata';
import type {
  OverviewHeroSize,
  OverviewImageFit,
  OverviewRecipeId,
  OverviewSlotRole,
} from '../types/overview';

export interface OverviewSizeBand {
  /** Narrow hero height as fraction of window height. */
  heightRatio: number;
  /** Wide layout media column width percent (0–100). */
  wideWidthPercent: number;
}

export interface OverviewRecipeSlotSpec {
  id: string;
  role: OverviewSlotRole;
  /** Roles that show label:value unit composition by default. */
  showDescriptor?: boolean;
  required?: boolean;
  /** When set, only these PropertyType-like kinds are valid (e.g. table). */
  allowedValueKinds?: ReadonlyArray<'table' | 'fileTable'>;
  maxLines?: number;
}

export interface OverviewRecipeDefinition {
  id: OverviewRecipeId;
  displayName: LocalizedText;
  supportsColumnSplit: boolean;
  chromePlacement: 'afterSubtitle' | 'afterHighlights' | 'beforeTable';
  hero: {
    defaultSourceKind: 'itemImage' | 'templateAsset' | 'none';
    defaultFit: OverviewImageFit;
    defaultSize: OverviewHeroSize;
    sizeBands: Record<OverviewHeroSize, OverviewSizeBand>;
    framing?: 'rect' | 'avatar';
    /** When true, editor forces hero.none and hides image controls. */
    forceNone?: boolean;
  };
  slots: readonly OverviewRecipeSlotSpec[];
}

const STANDARD_BANDS: Record<OverviewHeroSize, OverviewSizeBand> = {
  sm: { heightRatio: 0.28, wideWidthPercent: 34 },
  md: { heightRatio: 0.38, wideWidthPercent: 42 },
  lg: { heightRatio: 0.48, wideWidthPercent: 48 },
};

const SMALL_BANDS: Record<OverviewHeroSize, OverviewSizeBand> = {
  sm: { heightRatio: 0.2, wideWidthPercent: 28 },
  md: { heightRatio: 0.28, wideWidthPercent: 34 },
  lg: { heightRatio: 0.34, wideWidthPercent: 40 },
};

function highlightSlots(prefix: string, count: number): OverviewRecipeSlotSpec[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `${prefix}${i + 1}`,
    role: 'highlight' as const,
    showDescriptor: true,
  }));
}

function factSlots(count: number): OverviewRecipeSlotSpec[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `fact${i + 1}`,
    role: 'fact' as const,
    showDescriptor: true,
  }));
}

function actionSlots(count: number): OverviewRecipeSlotSpec[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `action${i + 1}`,
    role: 'action' as const,
    showDescriptor: true,
  }));
}

export const OVERVIEW_RECIPE_CATALOG: Record<OverviewRecipeId, OverviewRecipeDefinition> = {
  productHero: {
    id: 'productHero',
    displayName: { en: 'Product / inventory' },
    supportsColumnSplit: true,
    chromePlacement: 'afterHighlights',
    hero: {
      defaultSourceKind: 'itemImage',
      defaultFit: 'cover',
      defaultSize: 'md',
      sizeBands: STANDARD_BANDS,
      framing: 'rect',
    },
    slots: [
      { id: 'subtitle', role: 'subtitle' },
      { id: 'status', role: 'status', showDescriptor: true },
      ...highlightSlots('highlight', 4),
      ...factSlots(4),
      { id: 'body', role: 'body', maxLines: 8 },
    ],
  },
  personCard: {
    id: 'personCard',
    displayName: { en: 'Person / contact' },
    supportsColumnSplit: true,
    chromePlacement: 'afterSubtitle',
    hero: {
      defaultSourceKind: 'itemImage',
      defaultFit: 'cover',
      defaultSize: 'md',
      sizeBands: SMALL_BANDS,
      framing: 'avatar',
    },
    slots: [
      { id: 'subtitle', role: 'subtitle' },
      ...actionSlots(3),
      { id: 'status', role: 'status', showDescriptor: true },
      ...factSlots(6),
      { id: 'body', role: 'body', maxLines: 6 },
    ],
  },
  documentCover: {
    id: 'documentCover',
    displayName: { en: 'Document / book' },
    supportsColumnSplit: true,
    chromePlacement: 'afterHighlights',
    hero: {
      defaultSourceKind: 'itemImage',
      defaultFit: 'contain',
      defaultSize: 'md',
      sizeBands: STANDARD_BANDS,
      framing: 'rect',
    },
    slots: [
      { id: 'subtitle', role: 'subtitle' },
      { id: 'status', role: 'status', showDescriptor: true },
      ...highlightSlots('highlight', 3),
      ...factSlots(4),
      { id: 'body', role: 'body', maxLines: 10 },
    ],
  },
  statusLedger: {
    id: 'statusLedger',
    displayName: { en: 'Status / ledger' },
    supportsColumnSplit: true,
    chromePlacement: 'afterHighlights',
    hero: {
      defaultSourceKind: 'none',
      defaultFit: 'contain',
      defaultSize: 'sm',
      sizeBands: SMALL_BANDS,
      framing: 'rect',
    },
    slots: [
      { id: 'primaryAmount', role: 'highlight', showDescriptor: true },
      { id: 'status', role: 'status', showDescriptor: true },
      ...highlightSlots('highlight', 3),
      ...factSlots(4),
      { id: 'body', role: 'body', maxLines: 6 },
    ],
  },
  metricBrief: {
    id: 'metricBrief',
    displayName: { en: 'Metrics / recipe' },
    supportsColumnSplit: true,
    chromePlacement: 'afterHighlights',
    hero: {
      defaultSourceKind: 'itemImage',
      defaultFit: 'cover',
      defaultSize: 'md',
      sizeBands: STANDARD_BANDS,
      framing: 'rect',
    },
    slots: [
      { id: 'subtitle', role: 'subtitle' },
      ...highlightSlots('metric', 5),
      ...factSlots(4),
      { id: 'body', role: 'body', maxLines: 8 },
    ],
  },
  titleTable: {
    id: 'titleTable',
    displayName: { en: 'Title + table' },
    supportsColumnSplit: false,
    chromePlacement: 'beforeTable',
    hero: {
      defaultSourceKind: 'none',
      defaultFit: 'contain',
      defaultSize: 'sm',
      sizeBands: SMALL_BANDS,
      forceNone: true,
    },
    slots: [
      { id: 'subtitle', role: 'subtitle' },
      {
        id: 'table',
        role: 'table',
        required: true,
        allowedValueKinds: ['table', 'fileTable'],
      },
    ],
  },
};

export function getOverviewRecipe(
  recipeId: OverviewRecipeId,
): OverviewRecipeDefinition {
  return OVERVIEW_RECIPE_CATALOG[recipeId];
}

export function overviewRecipeSlotIds(recipeId: OverviewRecipeId): ReadonlySet<string> {
  return new Set(OVERVIEW_RECIPE_CATALOG[recipeId].slots.map((s) => s.id));
}

export function defaultOverviewPresentation(
  recipeId: OverviewRecipeId,
): import('../types/overview').ElementOverviewPresentation {
  const recipe = OVERVIEW_RECIPE_CATALOG[recipeId];
  const source =
    recipe.hero.forceNone || recipe.hero.defaultSourceKind === 'none'
      ? ({ kind: 'none' } as const)
      : recipe.hero.defaultSourceKind === 'templateAsset'
        ? ({ kind: 'templateAsset', assetId: '' } as const)
        : ({ kind: 'itemImage', preferredIndex: 0 } as const);
  return {
    recipeId,
    columnSplit: recipe.supportsColumnSplit ? 'auto' : 'stack',
    hero: {
      source,
      fit: recipe.hero.defaultFit,
      size: recipe.hero.defaultSize,
    },
    slots: {},
  };
}
